import { motion } from 'framer-motion';
import { Smartphone, Zap, Bell, Gift, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';

export function AppDownloadBanner() {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const benefits = [
    { icon: Zap, text: 'Faster Checkout' },
    { icon: Bell, text: 'Price Drop Alerts' },
    { icon: Gift, text: 'Exclusive Offers' },
  ];

  return (
    <section className="relative bg-gradient-to-r from-foreground via-foreground/95 to-foreground overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, hsl(var(--primary)) 1px, transparent 1px),
                           radial-gradient(circle at 80% 50%, hsl(var(--aurakart-yellow)) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      {/* Dismiss button - mobile only */}
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-3 right-3 p-1.5 hover:bg-card/10 rounded-full transition-colors lg:hidden z-10"
      >
        <X className="h-4 w-4 text-card/70" />
      </button>

      <div className="container mx-auto px-4 py-8 lg:py-12">
        <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-12">
          {/* Phone mockup */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative flex-shrink-0"
          >
            <div className="relative w-40 h-72 lg:w-48 lg:h-80">
              {/* Phone frame */}
              <div className="absolute inset-0 bg-gradient-to-b from-card/20 to-card/5 rounded-[2rem] border-4 border-card/20 overflow-hidden">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-foreground rounded-full" />
                <div className="mt-8 px-3 space-y-2">
                  <div className="bg-card/20 rounded-lg h-20" />
                  <div className="flex gap-2">
                    <div className="bg-primary/40 rounded h-12 flex-1" />
                    <div className="bg-aurakart-yellow/40 rounded h-12 flex-1" />
                  </div>
                  <div className="bg-card/20 rounded-lg h-16" />
                  <div className="bg-aurakart-green/40 rounded-lg h-10" />
                </div>
              </div>
              {/* Glow effect */}
              <div className="absolute -inset-4 bg-primary/20 blur-2xl rounded-full" />
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex-1 text-center lg:text-left text-card"
          >
            <div className="inline-flex items-center gap-2 bg-card/10 px-3 py-1.5 rounded-full mb-4">
              <Smartphone className="h-4 w-4" />
              <span className="text-sm font-medium">Download the App</span>
            </div>
            
            <h2 className="text-2xl lg:text-3xl font-bold mb-3">
              Get the Best Shopping Experience
            </h2>
            <p className="text-card/80 mb-6 max-w-md mx-auto lg:mx-0">
              Download the AuraKart app for exclusive deals, faster checkout, and personalized recommendations.
            </p>

            {/* Benefits */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-6">
              {benefits.map((benefit) => (
                <div key={benefit.text} className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-card/10 flex items-center justify-center">
                    <benefit.icon className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-medium">{benefit.text}</span>
                </div>
              ))}
            </div>

            {/* Download buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <Button
                variant="outline"
                size="lg"
                className="bg-card text-foreground hover:bg-card/90 border-0 gap-2"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <div className="text-left">
                  <div className="text-[10px] leading-none opacity-80">Download on</div>
                  <div className="text-sm font-semibold leading-tight">App Store</div>
                </div>
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                className="bg-card text-foreground hover:bg-card/90 border-0 gap-2"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                </svg>
                <div className="text-left">
                  <div className="text-[10px] leading-none opacity-80">Get it on</div>
                  <div className="text-sm font-semibold leading-tight">Google Play</div>
                </div>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
